import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DeviceUtils from '../utils/DeviceUtils';
import API from '../services/API';
import MainFormComponent from './MainFormComponent';
import ResultPageComponent from './ResultPageComponent';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import SystemHeader from './SystemHeader';

// Main App Component
const DynamicFormSystem = ({ userInfo, onLogout, totalPoints = 0, onRefreshPoints }) => {
  const { campaignCode } = useParams();
  const navigate = useNavigate();
  
  const [currentPage, setCurrentPage] = useState('login');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(''); // eslint-disable-line no-unused-vars
  const [mainPageUrl, setMainPageUrl] = useState('');
  const [resultPageUrl, setResultPageUrl] = useState('');
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [pageTransition, setPageTransition] = useState(false);
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [userInfoLocal, setUserInfoLocal] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  // Sử dụng userInfo từ AuthWrapper nếu có, nếu không thì dùng local state
  const finalUserInfo = userInfo || userInfoLocal;
  const finalPhoneNumber = userInfo?.phoneNumber || phoneNumber;

  // Fetch campaign information and update URLs
  const fetchCampaignInfo = useCallback(async (campaignCode) => {
    if (!campaignCode) return;
    
    setCampaignLoading(true);
    
    try {
      console.log('🚀 DynamicFormSystem - Fetching campaign info for code:', campaignCode);
      const result = await API.getCampaignInfo(campaignCode);
      
      if (result.success) {
        setCampaignInfo(result.campaign);
        console.log('✅ DynamicFormSystem - Campaign info loaded:', result.campaign);
        
        // Update URLs from campaign (campaign URLs take priority)
        console.log('🔍 DynamicFormSystem - Campaign result:', result);
        console.log('🔍 DynamicFormSystem - Current URLs before update:', { mainPageUrl, resultPageUrl });
        
        if (result.mainUrl) {
          console.log('🔄 DynamicFormSystem - Updating main URL from campaign:', result.mainUrl);
          setMainPageUrl(result.mainUrl);
        } else {
          console.log('⚠️ DynamicFormSystem - No mainUrl found in campaign result');
        }
        
        if (result.successUrl) {
          console.log('🔄 DynamicFormSystem - Updating success URL from campaign:', result.successUrl);
          setResultPageUrl(result.successUrl);
        } else {
          console.log('⚠️ DynamicFormSystem - No successUrl found in campaign result');
        }
      }
    } catch (error) {
      console.error('❌ DynamicFormSystem - Failed to fetch campaign info:', error);
      // Don't block the app if campaign fetch fails
    } finally {
      setCampaignLoading(false);
    }
  }, [mainPageUrl, resultPageUrl]);

  // Check for existing token on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const mainUrl = urlParams.get('mainUrl');
    const resultUrl = urlParams.get('resultUrl');

    // Set iframe URLs from URL parameters
    if (mainUrl) setMainPageUrl(decodeURIComponent(mainUrl));
    if (resultUrl) setResultPageUrl(decodeURIComponent(resultUrl));

    // Check for campaign code from React Router params
    if (campaignCode) {
      console.log('🔍 DynamicFormSystem - Found campaign code from params:', campaignCode);
      fetchCampaignInfo(campaignCode);
    }

    // Nếu có userInfo từ AuthWrapper, sử dụng luôn
    if (userInfo) {
      setPhoneNumber(userInfo.phoneNumber);
      setUserInfoLocal(userInfo);
      setCurrentPage('main');
    } else if (token) {
      // Fallback: kiểm tra token như cũ
      const verification = DeviceUtils.verifyToken(token);
      if (verification.isValid) {
        setPhoneNumber(verification.phoneNumber);
        setCurrentPage('main');
      } else {
        setCurrentPage('login');
        setError('Session không hợp lệ hoặc đã hết hạn');
      }
    } else {
      setCurrentPage('login');
    }

    setIsInitializing(false);
  }, [campaignCode, fetchCampaignInfo, userInfo]);

  // Debug: Log when URLs change
  useEffect(() => {
    console.log('🔍 DynamicFormSystem - URLs updated:', {
      mainPageUrl,
      resultPageUrl,
      campaignInfo: campaignInfo ? 'Loaded' : 'Not loaded'
    });
  }, [mainPageUrl, resultPageUrl, campaignInfo]);

  // Login and Register page handlers
  const handleLoginSuccess = async () => {
    await smoothPageTransition('main', 400);
  };

  const handleRegisterSuccess = async () => {
    await smoothPageTransition('main', 400);
  };

  const handleGoToRegister = async () => {
    console.log('🔄 handleGoToRegister called - transitioning to register page');
    await smoothPageTransition('register', 300);
    console.log('✅ handleGoToRegister completed');
  };

  const handleBackToLogin = async () => {
    // Reset state
    setPhoneNumber('');
    setError('');
    setUserInfoLocal(null);
    setSubmittedFormData(null);
    
    await smoothPageTransition('login', 300);
  };

  // Handle form submission from MainFormComponent
  const handleFormSubmit = async (formData) => {
    try {
      console.log('🎯 DynamicFormSystem handleFormSubmit called with:', formData);
      
      // You can add custom processing here
      console.log('📊 Processing form data:', formData);
      
      // Store form data for result page
      setSubmittedFormData(formData);
      console.log('💾 Form data stored in state');
      
      // Simulate API processing time for better UX
      console.log('⏳ Simulating API processing...');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Simulate API call to save form data
      // await API.saveFormData(formData);
      
      // Redirect to result page with form data
      console.log('🔄 Preparing redirect to result page...');
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const mainUrl = urlParams.get('mainUrl');
      const resultUrl = urlParams.get('resultUrl');
      
      const params = new URLSearchParams({
        token,
        mainUrl: mainUrl,
        resultUrl: resultUrl,
        success: 'true',
        formData: encodeURIComponent(JSON.stringify(formData))
      });
      
      console.log('📤 Redirecting to result with params:', params.toString());
      window.history.replaceState({}, '', `/result?${params.toString()}`);
      await smoothPageTransition('result', 400);
      
      console.log('✅ Redirect to result completed');
      return { success: true };
    } catch (error) {
      console.error('❌ Form processing error:', error);
      throw error;
    }
  };

  // Handle logout from MainFormComponent
  const handleMainFormLogout = async () => {
    // Reset state
    setPhoneNumber('');
    setError('');
    setUserInfoLocal(null);
    setSubmittedFormData(null);
    
    // Sử dụng onLogout từ AuthWrapper nếu có
    if (onLogout) {
      onLogout();
    } else {
      // Fallback: Navigate back to campaign list
      navigate('/chien-dich');
    }
  };

  // Smooth page transition function
  const smoothPageTransition = async (newPage, delay = 300) => {
    setPageTransition(true);
    
    // Small delay for smooth transition
    await new Promise(resolve => setTimeout(resolve, delay));
    
    setCurrentPage(newPage);
    
    // Reset transition state after page change
    setTimeout(() => {
      setPageTransition(false);
    }, 100);
  };

  // Make setCurrentPage available globally for MainFormComponent
  useEffect(() => {
    window.setCurrentPage = setCurrentPage;
    window.smoothPageTransition = smoothPageTransition;
    return () => {
      delete window.setCurrentPage;
      delete window.smoothPageTransition;
    };
  }, []);

  // Main Page Template using MainFormComponent
  const MainPageTemplate = () => {
    const currentToken = new URLSearchParams(window.location.search).get('token');
    const accessToken = new URLSearchParams(window.location.search).get('accessToken');
    
    // Add token to iframe URL if available
    const getIframeUrlWithToken = (url) => {
      if (!url || !currentToken) return url;
      
      try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('token', currentToken);
        if (accessToken) {
          urlObj.searchParams.set('accessToken', accessToken);
        }
        return urlObj.toString();
      } catch (error) {
        console.warn('Could not add token to URL:', error);
        return url;
      }
    };
    
    const iframeUrlWithToken = getIframeUrlWithToken(mainPageUrl);
    
    // Extra info for header - campaign name
    const extraInfo = campaignInfo && (
      <div className="text-center">
        <p className="text-lg font-semibold text-gray-900">{campaignInfo.name}</p>
        <p className="text-sm text-gray-500">Chiến dịch hiện tại</p>
      </div>
    );
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* System Header */}
        <SystemHeader
          userInfo={finalUserInfo}
          onLogout={handleMainFormLogout}
          totalPoints={totalPoints}
          pageTitle={`Chi tiết chiến dịch ${campaignCode}`}
          pageSubtitle="Điền thông tin để tham gia"
          extraInfo={extraInfo}
          onRefreshPoints={onRefreshPoints}
        />
        
        {/* Main Form Content */}
        <MainFormComponent
          phoneNumber={finalPhoneNumber}
          userInfo={finalUserInfo}
          accessToken={accessToken}
          token={currentToken}
          customFormUrl={iframeUrlWithToken}
          resultPageUrl={resultPageUrl}
          onSubmit={handleFormSubmit}
          onLogout={handleMainFormLogout}
          campaignInfo={campaignInfo}
        />
      </div>
    );
  };

  // Handle back to main from ResultPageComponent
  const handleResultBackToMain = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const mainUrl = urlParams.get('mainUrl');
    const resultUrl = urlParams.get('resultUrl');
    
    const params = new URLSearchParams({
      token,
      mainUrl: mainUrl,
      resultUrl: resultUrl
    });
    
    window.history.replaceState({}, '', `?${params.toString()}`);
    await smoothPageTransition('main', 300);
  };

  // Handle logout from ResultPageComponent
  const handleResultLogout = async () => {
    // Reset state
    setPhoneNumber('');
    setError('');
    setUserInfoLocal(null);
    setSubmittedFormData(null);
    
    // Sử dụng onLogout từ AuthWrapper nếu có
    if (onLogout) {
      onLogout();
    } else {
      // Fallback: Navigate back to campaign list
      navigate('/chien-dich');
    }
  };

  // Result Page Template using ResultPageComponent
  const ResultPageTemplate = () => {
    const currentToken = new URLSearchParams(window.location.search).get('token');
    const accessToken = new URLSearchParams(window.location.search).get('accessToken');
    const urlFormData = new URLSearchParams(window.location.search).get('formData');
    
    console.log('🎯 ResultPageTemplate - URL formData:', urlFormData);
    console.log('🎯 ResultPageTemplate - State submittedFormData:', submittedFormData);
    
    // Add token to result iframe URL if available
    const getResultIframeUrlWithToken = (url) => {
      if (!url || !currentToken) return url;
      
      try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('token', currentToken);
        if (accessToken) {
          urlObj.searchParams.set('accessToken', accessToken);
        }
        return urlObj.toString();
      } catch (error) {
        console.warn('Could not add token to result URL:', error);
        return url;
      }
    };
    
    const resultIframeUrlWithToken = getResultIframeUrlWithToken(resultPageUrl);
    
    // Use formData from URL if available, otherwise use submittedFormData
    let formDataToPass = submittedFormData;
    if (urlFormData) {
      try {
        formDataToPass = JSON.parse(decodeURIComponent(urlFormData));
        console.log('✅ Parsed formData from URL:', formDataToPass);
      } catch (error) {
        console.warn('❌ Could not parse formData from URL:', error);
      }
    }
    
    console.log('📤 Passing formData to ResultPageComponent:', formDataToPass);
    
    // Extra info for header - campaign name and success status
    const extraInfo = campaignInfo && (
      <div className="text-center">
        <p className="text-lg font-semibold text-green-600">🎉 Hoàn thành</p>
        <p className="text-sm text-gray-500">{campaignInfo.name}</p>
      </div>
    );
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100">
        {/* System Header */}
        <SystemHeader
          userInfo={finalUserInfo}
          onLogout={handleResultLogout}
          totalPoints={totalPoints}
          pageTitle={`Kết quả chiến dịch ${campaignCode}`}
          pageSubtitle="Xem kết quả tham gia của bạn"
          extraInfo={extraInfo}
          onRefreshPoints={onRefreshPoints}
        />
        
        {/* Result Content */}
        <ResultPageComponent
          phoneNumber={finalPhoneNumber}
          token={currentToken}
          customResultUrl={resultIframeUrlWithToken}
          mainPageUrl={mainPageUrl}
          formData={formDataToPass}
          onBackToMain={handleResultBackToMain}
          onLogout={handleResultLogout}
        />
      </div>
    );
  };

  // Show loading screen during initialization
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang khởi tạo hệ thống...</p>
        </div>
      </div>
    );
  }

  // Show campaign loading state
  if (campaignLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải thông tin campaign...</p>
        </div>
      </div>
    );
  }

  // Page transition overlay
  if (pageTransition) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            {/* Animated circles */}
            <div className="w-16 h-16 relative mx-auto mb-6">
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-purple-200 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            {/* Loading text with animation */}
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-700 animate-pulse">Đang chuyển trang</p>
              <div className="flex justify-center space-x-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render current page with fade-in animation
  const renderCurrentPage = () => {
    let PageComponent;
    
    switch (currentPage) {
      case 'login':
        PageComponent = () => (
          <LoginPage
            onLogin={handleLoginSuccess}
            onRegister={handleGoToRegister}
            setMainPageUrl={setMainPageUrl}
            setResultPageUrl={setResultPageUrl}
            setPhoneNumber={setPhoneNumber}
            setError={setError}
            error={error}
            loading={loading}
            setLoading={setLoading}
            setUserInfo={setUserInfoLocal}
            mainPageUrl={mainPageUrl}
            resultPageUrl={resultPageUrl}
            campaignInfo={campaignInfo}
          />
        );
        break;
      case 'register':
        PageComponent = () => (
          <RegisterPage
            phoneNumber={phoneNumber}
            onRegister={handleRegisterSuccess}
            onBackToLogin={handleBackToLogin}
            setMainPageUrl={setMainPageUrl}
            setResultPageUrl={setResultPageUrl}
            mainPageUrl={mainPageUrl}
            resultPageUrl={resultPageUrl}
            setPhoneNumber={setPhoneNumber}
            setUserInfo={setUserInfoLocal}
            setError={setError}
            loading={loading}
            setLoading={setLoading}
            campaignInfo={campaignInfo}
          />
        );
        break;
      case 'main':
        PageComponent = MainPageTemplate;
        break;
      case 'result':
        PageComponent = ResultPageTemplate;
        break;
      default:
        PageComponent = () => (
          <LoginPage
            onLogin={handleLoginSuccess}
            onRegister={handleGoToRegister}
            setMainPageUrl={setMainPageUrl}
            setResultPageUrl={setResultPageUrl}
            setPhoneNumber={setPhoneNumber}
            setError={setError}
            error={error}
            loading={loading}
            setLoading={setLoading}
          />
        );
    }
    
    return (
      <div className="animate-fade-in">
        <PageComponent />
      </div>
    );
  };

  return renderCurrentPage();
};

export default DynamicFormSystem;
